import {Injectable} from '@angular/core';
import {ModalController, NavController} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import {AuthApiService} from '../Api/Auth/auth-api.service';
import {UserSessionDto} from '../../Models/User/user-session.interface';
import {ToastManagerService} from '../Platform/Ionic/toast-manager.service';
import {FiltersKeyEnum} from '../../Shared/Enum/filters-key.enum';
import {FiltersModelService} from '../Models/Core/filters-model.service';
import {WidgetFilters} from '../../Models/Widgets/widget-filters.model';
import {HapticsImpactManagerService} from '../Platform/Capacitor/haptics-impact-manager.service';
import {FavoritesWidgetsService} from '../Models/Widgets/favorites-widgets.service';
import {PermissionsManagerService} from './permissions-manager.service';
import {WidgetDto, WidgetGroupDto} from '../../Models/Widgets/widget.interface';
import {FavoriteWidgetDto} from '../../Models/Widgets/favorite-widget.interface';
import {WidgetsTypesEnum} from '../../Shared/Enum/widgets-types.enum';
import {RoutesEnum} from '../../Shared/Enum/routes.enum';
import {TransferStatusEnum} from '../../Shared/Enum/transfer-status.enum';
import {CommunicationTypesEnum} from '../../Shared/Enum/communication-types.enum';
import {OrderStatusEnum} from '../../Shared/Enum/order-status.enum';
import {rehydrateFavoriteWidgets, toFavoriteWidgets} from '../../Shared/Utils/favorites.utils';

@Injectable({
  providedIn: 'root'
})
export class WidgetsManagerService {

  public widgetsGroups: WidgetGroupDto[] = [];
  public favoritesWidgets: WidgetDto[] = [];

  public filters: WidgetFilters | null = null;

  public loadedWidgets: boolean = false;

  constructor(
    public favoritesWidgetsModel: FavoritesWidgetsService,
    protected auth: AuthApiService,
    protected navCtrl: NavController,
    protected modalCtrl: ModalController,
    protected toastCtrl: ToastManagerService,
    protected filtersService: FiltersModelService,
    protected hapticsImpactManager: HapticsImpactManagerService,
    protected permissionsManager: PermissionsManagerService,
    protected translate: TranslateService,
  ) {
  }


  /**
   * Obtener los widgets
   *
   */
  public async getWidgets() {
    await this.getWidgetsFilters();

    this.widgetsGroups = this.initWidgets();
    this.checkWidgetsGroup();

    await this.setFavoriteItemsToGroup();
    this.loadedWidgets = true;
  }


  /**
   * Limpiar datos de los widgets
   *
   */
  public cleanWidgetsData() {
    this.widgetsGroups = [];
    this.favoritesWidgets = [];

    this.filters = null;
    this.loadedWidgets = false;
  }


  /**
   * Guardar el orden de los widgets favoritos
   *
   */
  public async saveFavoritesLayout(widgets: WidgetDto[]) {
    this.favoritesWidgets = widgets;
    await this.saveFavoriteWidgets();
  }


  /**
   * Añadir un widget a favoritos fijando su columna para que la posición sea estable
   *
   */
  public async addFavoriteWidget(widget: WidgetDto) {
    const col: 0 | 1 = this.nextFreeColumn(this.favoritesWidgets);
    const favorite: WidgetDto = widget.fullWidth ? {...widget, favorite: true} : {...widget, favorite: true, col};

    this.favoritesWidgets = [...this.favoritesWidgets, favorite];
    await this.saveFavoriteWidgets();
  }


  /**
   * Eliminar un widget de favoritos
   *
   */
  public async removeFavoriteWidget(id: string) {
    this.favoritesWidgets = this.favoritesWidgets.filter((widget) => widget.id !== id);
    await this.saveFavoriteWidgets();
  }


  /**
   * Eliminar todos los widgets de favoritos
   *
   */
  public async clearFavoriteWidgets() {
    this.favoritesWidgets = [];
    await this.saveFavoriteWidgets();
  }


  /**
   * Obtener los filtros
   *
   */
  public async getWidgetsFilters() {
    const response = await this.filtersService.listKey(FiltersKeyEnum.widgets)
    this.filters = new WidgetFilters(response);

    await this.filtersService.saveKey(this.filters, FiltersKeyEnum.widgets);
  }


  /**
   * Guardar filtros
   *
   */
  public async saveWidgetsFilters() {
    await this.filtersService.saveKey(this.filters, FiltersKeyEnum.widgets);
  }


  /**
   * Abrir filtros
   *
   */
  public async openWidgetsFilters(): Promise<boolean> {
    const {WidgetsFiltersPage} = await import('../../Pages/Widgets/widgets-filters/widgets-filters.page');

    await this.hapticsImpactManager.hapticsImpactMedium();

    const modal = await this.modalCtrl.create({
      component: WidgetsFiltersPage,
      componentProps: {
        filters: this.filters,
      },
      cssClass: 'yu-modal',
    });

    await modal.present();

    const {data} = await modal.onDidDismiss();

    if (data) {
      this.filters = data;
      this.saveWidgetsFilters();
    }

    return !!data;
  }


  /**
   * Abrir modal para establecer los widgets favoritos
   *
   */
  public async openModalSetFavoriteWidgets() {
    const {WidgetsStorePage} = await import('../../Pages/Widgets/widgets-store/widgets-store.page');
    
    await this.hapticsImpactManager.hapticsImpactMedium();

    const modal = await this.modalCtrl.create({
      component: WidgetsStorePage,
      cssClass: 'yu-modal',
    });

    await modal.present();
    await modal.onDidDismiss();

    this.favoritesWidgets = [...this.favoritesWidgets];
  }


  /**
   * Asignar los widgets favoritos al grupo que pertenece
   *
   */
  public async setFavoriteItemsToGroup() {
    const storedWidgets: FavoriteWidgetDto[] | null = await this.favoritesWidgetsModel.getForUser();

    this.favoritesWidgets = storedWidgets ? this.rehydrateFavorites(storedWidgets) : [];

    const favoritesWidgetsIds = new Set(this.favoritesWidgets.map((favWidget) => favWidget.id));

    this.widgetsGroups = this.widgetsGroups.map((group) => ({
      ...group,
      widgets: group.widgets.map((widget) => ({
        ...widget,
        favorite: favoritesWidgetsIds.has(widget.id),
      }))
    }));
  }


  /**
   * Acción sobre el widget
   *
   */
  public doWidgetAction(widget: WidgetDto) {
    const targetGroup = this.widgetsGroups.find((group) => group.widgets.some((w) => w.id === widget.id));
    const targetWidget = targetGroup?.widgets.find((w) => w.id === widget.id);

    if (!targetWidget) {
      return;
    }

    if (!targetWidget.hasPermission) {
      this.toastCtrl.showWarningToast(this.translate.instant('COMMON.NO_PERMISSION'));
      return;
    }

    this.goToView(targetWidget.route, {...targetWidget.data, ...widget.data, widgetFilters: this.filters});
  }


  /**
   * Ir a la vista
   *
   */
  private goToView(path: string, ...params: any[]) {
    this.navCtrl.navigateForward(path, {
      animated: true,
      queryParams: params ? params[0] : null,
      queryParamsHandling: 'merge'
    });
  }


  /**
   * Comprobar que los widgets de cada grupo están activos
   *
   */
  private checkWidgetsGroup() {
    this.widgetsGroups = this.widgetsGroups
      .filter((group) => group.active)
      .map((group) => ({
        ...group,
        active: group.widgets.some((item) => item.hasPermission),
        widgets: group.widgets.filter((item) => !item.hidden),
      }));
  }


  /**
   * Re-hidratar los favoritos guardados contra la definición canónica
   *
   */
  private rehydrateFavorites(storedWidgets: FavoriteWidgetDto[]): WidgetDto[] {
    return rehydrateFavoriteWidgets(storedWidgets, this.widgetsGroups.flatMap((group) => group.widgets));
  }


  /**
   * Guardar los favoritos del usuario conservando solo lo que él decide
   *
   */
  private saveFavoriteWidgets(): Promise<FavoriteWidgetDto[]> {
    return this.favoritesWidgetsModel.saveForUser(toFavoriteWidgets(this.favoritesWidgets));
  }


  /**
   * Inicializar los widgets
   *
   */
  private initWidgets(): WidgetGroupDto[] {
    const user: UserSessionDto | null = this.auth.getUserSession();

    return [
      {
        id: 'widgets-purchases',
        active: true,
        name: this.translate.instant('MODULES.MENU.PURCHASES'),
        widgets: [
          {
            id: 'widgets-purchases-' + 1,
            name: this.translate.instant('WIDGETS.PURCHASES.LAST_ORDERS'),
            type: WidgetsTypesEnum.lastOrders,
            fullWidth: true,
            route: RoutesEnum.orders,
            hasPermission: this.permissionsManager.checkPermission('pedidos', 'ver'),
          },
          {
            id: 'widgets-purchases-' + 2,
            name: this.translate.instant('WIDGETS.PURCHASES.PURCHASES_SCHEDULED'),
            type: WidgetsTypesEnum.purchasesScheduledPendents,
            route: RoutesEnum.tasks,
            hasPermission: this.permissionsManager.checkPermission('tareas', 'ver'),
            hidden: !!user?.force_compras,
          },
          {
            id: 'widgets-purchases-' + 3,
            name: this.translate.instant('WIDGETS.PURCHASES.NOT_RECEIVED_ORDERS'),
            type: WidgetsTypesEnum.notReceivedOrders,
            route: RoutesEnum.orders,
            data: {menu: 'pendents'},
            hasPermission: this.permissionsManager.checkPermission('pedidos', 'ver'),
          },
          {
            id: 'widgets-purchases-' + 4,
            name: this.translate.instant('WIDGETS.PURCHASES.NOT_SENT_ORDERS'),
            type: WidgetsTypesEnum.notSentOrders,
            route: RoutesEnum.orders,
            data: {state: OrderStatusEnum.pendent},
            hasPermission: this.permissionsManager.checkPermission('pedidos', 'ver'),
          }
        ]
      },
      {
        id: 'widgets-stocks',
        active: !user?.force_compras,
        name: this.translate.instant('MODULES.MENU.WAREHOUSES'),
        widgets: [
          {
            id: 'widgets-stocks-' + 1,
            name: this.translate.instant('WIDGETS.STOCKS.PENDING_TRANSFERS'),
            type: WidgetsTypesEnum.pendentsTransfers,
            route: RoutesEnum.transfers,
            data: {state: TransferStatusEnum.sent},
            hasPermission: this.permissionsManager.checkPermission('traslados', 'ver'),
          },
          {
            id: 'widgets-stocks-' + 2,
            name: this.translate.instant('WIDGETS.STOCKS.INVENTORY_SCHEDULED'),
            type: WidgetsTypesEnum.inventoryScheduledPendents,
            route: RoutesEnum.tasks,
            hasPermission: this.permissionsManager.checkPermission('tareas', 'ver'),
          }
        ]
      },
      {
        id: 'widgets-rrhh',
        active: !user?.force_compras,
        name: this.translate.instant('MODULES.MENU.HR'),
        widgets: [
          {
            id: 'widgets-rrhh-' + 1,
            name: this.translate.instant('WIDGETS.HR.LAST_CLOCK_IN'),
            type: WidgetsTypesEnum.lastClockIn,
            fullWidth: true,
            route: RoutesEnum.clockIn,
            hasPermission: this.permissionsManager.checkPermission('fichajes', 'ver'),
          },
        ]
      },
      {
        id: 'widgets-finances',
        active: !user?.force_compras,
        name: this.translate.instant('MODULES.MENU.FINANCES'),
        widgets: [
          {
            id: 'widgets-finances-' + 1,
            name: this.translate.instant('WIDGETS.FINANCES.SALES'),
            type: WidgetsTypesEnum.ebitdaIncome,
            fullWidth: true,
            route: RoutesEnum.ebitda,
            hasPermission: this.permissionsManager.checkPermission('ebitda', 'ver'),
          },
          {
            id: 'widgets-finances-' + 2,
            name: this.translate.instant('WIDGETS.FINANCES.EBITDA'),
            type: WidgetsTypesEnum.ebitdaReport,
            route: RoutesEnum.ebitda,
            hasPermission: this.permissionsManager.checkPermission('ebitda', 'ver'),
          },
          {
            id: 'widgets-finances-' + 3,
            name: this.translate.instant('WIDGETS.FINANCES.PURCHASES'),
            type: WidgetsTypesEnum.ebitdaPurchases,
            route: RoutesEnum.ebitda,
            hasPermission: this.permissionsManager.checkPermission('ebitda', 'ver'),
          },
          {
            id: 'widgets-finances-' + 4,
            name: this.translate.instant('WIDGETS.FINANCES.PERSONAL'),
            type: WidgetsTypesEnum.ebitdaPersonal,
            route: RoutesEnum.ebitda,
            hasPermission: this.permissionsManager.checkPermission('ebitda', 'ver'),
          },
          {
            id: 'widgets-finances-' + 5,
            name: this.translate.instant('WIDGETS.FINANCES.GENERAL'),
            type: WidgetsTypesEnum.ebitdaGeneral,
            route: RoutesEnum.ebitda,
            hasPermission: this.permissionsManager.checkPermission('ebitda', 'ver'),
          },
        ]
      },
      {
        id: 'widgets-documents-review',
        active: !user?.force_compras,
        name: this.translate.instant('MODULES.MENU.DOCUMENTS_REVIEW'),
        widgets: [
          {
            id: 'widgets-documents-review-' + 1,
            name: this.translate.instant('WIDGETS.DOCUMENTS_REVIEW.DOCUMENTS_TO_REVIEW'),
            type: WidgetsTypesEnum.documentsReview,
            fullWidth: true,
            route: RoutesEnum.documentsReview,
            hasPermission: this.permissionsManager.checkPermission('revision_albaranes', 'ver')
              || this.permissionsManager.checkPermission('revision_gastos', 'ver')
              || this.permissionsManager.checkPermission('revision_nominas', 'ver')
              || this.permissionsManager.checkPermission('revision_otros', 'ver'),
          },
          {
            id: 'widgets-documents-review-' + 2,
            name: this.translate.instant('WIDGETS.DOCUMENTS_REVIEW.DELIVERY_NOTES'),
            type: WidgetsTypesEnum.deliveryNotesReview,
            route: RoutesEnum.documentsReview,
            data: {menu: 'delivery-notes'},
            hasPermission: this.permissionsManager.checkPermission('revision_albaranes', 'ver'),
          },
          {
            id: 'widgets-documents-review-' + 3,
            name: this.translate.instant('WIDGETS.DOCUMENTS_REVIEW.EXPENSES'),
            type: WidgetsTypesEnum.expensesReview,
            route: RoutesEnum.documentsReview,
            data: {menu: 'expenses'},
            hasPermission: this.permissionsManager.checkPermission('revision_gastos', 'ver'),
          },
          {
            id: 'widgets-documents-review-' + 4,
            name: this.translate.instant('WIDGETS.DOCUMENTS_REVIEW.PAYROLLS'),
            type: WidgetsTypesEnum.payrollsReview,
            route: RoutesEnum.documentsReview,
            data: {menu: 'payrolls'},
            hasPermission: this.permissionsManager.checkPermission('revision_nominas', 'ver'),
          },
          {
            id: 'widgets-documents-review-' + 5,
            name: this.translate.instant('WIDGETS.DOCUMENTS_REVIEW.UNCLASSIFIED'),
            type: WidgetsTypesEnum.unclassifiedReview,
            route: RoutesEnum.documentsReview,
            data: {menu: 'unclassified'},
            hasPermission: this.permissionsManager.checkPermission('revision_otros', 'ver'),
          },
        ]
      },
      {
        id: 'widgets-communication',
        active: !user?.force_compras,
        name: this.translate.instant('MODULES.MENU.COMMUNICATION'),
        widgets: [
          {
            id: 'widgets-communication-' + 1,
            name: this.translate.instant('WIDGETS.COMMUNICATION.UNREAD'),
            type: WidgetsTypesEnum.unreadCommunication,
            fullWidth: true,
            route: RoutesEnum.communication,
            hasPermission: this.permissionsManager.checkPermission('comunicacion', 'ver'),
          },
          {
            id: 'widgets-communication-' + 2,
            name: this.translate.instant('WIDGETS.COMMUNICATION.PENDING_BREAKDOWNS'),
            type: WidgetsTypesEnum.pendentsBreakdowns,
            route: RoutesEnum.communication,
            data: {type: CommunicationTypesEnum.breakdowns},
            hasPermission: this.permissionsManager.checkPermission('comunicacion', 'ver'),
          },
        ]
      },
      {
        id: 'widgets-tasks',
        active: !user?.force_compras,
        name: this.translate.instant('CORE.TABS.TASKS'),
        widgets: [
          {
            id: 'widgets-tasks-' + 1,
            name: this.translate.instant('WIDGETS.TASKS.COMPLETED_CHECKLISTS'),
            type: WidgetsTypesEnum.completedChecklists,
            route: RoutesEnum.tasks,
            data: {type: 'checklist'},
            hasPermission: this.permissionsManager.checkPermission('checklists', 'ver'),
          },
          {
            id: 'widgets-tasks-' + 2,
            name: this.translate.instant('WIDGETS.TASKS.COMPLETED_APPCC'),
            type: WidgetsTypesEnum.completedAppcc,
            route: RoutesEnum.tasks,
            data: {type: 'appcc'},
            hasPermission: this.permissionsManager.checkPermission('appcc', 'ver'),
          },
          {
            id: 'widgets-tasks-' + 3,
            name: this.translate.instant('WIDGETS.TASKS.COMPLETED_AUDITS'),
            type: WidgetsTypesEnum.completedAudit,
            route: RoutesEnum.tasks,
            data: {type: 'audit'},
            hasPermission: this.permissionsManager.checkPermission('auditorias', 'ver'),
          },
          {
            id: 'widgets-tasks-' + 4,
            name: this.translate.instant('WIDGETS.TASKS.VALUATION_AUDITS'),
            type: WidgetsTypesEnum.valuationAudit,
            route: RoutesEnum.tasks,
            data: {type: 'audit'},
            hasPermission: this.permissionsManager.checkPermission('auditorias', 'ver'),
          },
        ]
      },
    ];
  }


  /**
   * Calcular la columna libre de la última fila al añadir un widget de media anchura al final
   *
   */
  private nextFreeColumn(widgets: WidgetDto[]): 0 | 1 {
    let leftTaken = false;
    let rightTaken = false;

    const openNewRow = () => {
      leftTaken = false;
      rightTaken = false;
    };

    widgets.forEach((widget) => {

      if (widget.fullWidth) {
        openNewRow();
        return;
      }

      const desired = widget.col ?? (leftTaken ? 1 : 0);

      if (desired === 0) {

        if (leftTaken) {
          openNewRow();
        }

        leftTaken = true;

      } else {

        if (rightTaken) {
          openNewRow();
        }

        rightTaken = true;
        openNewRow();
      }
    });

    return leftTaken ? 1 : 0;
  }

}
